/*
 * Copyright © 2026 Medaica, Inc
 *
 * All rights reserved.
 *
 * This code is confidential and proprietary information belonging to Medaica, Inc.
 * Unauthorized copying, distribution, or use of this code, in whole or in part,
 * is strictly prohibited, and may constitute a violation of intellectual property rights.
 *
 * If you have received this code in error, please notify the owner immediately
 * at support@medaica.com and delete this file from your system.
 */

// pcg-report core: per-recording selection on the timing cards (no DOM).
// Source: cardiac_report_v0_5_8.html, the prototype's clinical-review
// refinement — isTimingReviewFeature, medianNumber, selectedTimingSources,
// timingSourceValue, timingSourceSelectionSummary, worstStatus,
// recalculateTimingFeatureFromSources, toggleTimingSource and
// timingRangeMarkerSpec (here `timingRangeOf`).
//
// The four timing cards (Heart Rate, SYS:DIA Ratio, SYS:DIA Duration, Cycle
// Regularity) carry `timingSourceRecordings`: one record per recording the
// review accepted for Timing (the roll-up's `sourceRecordings`), each with
// the recording's own numbers and whether the reviewer includes it on that
// card. The card's value is derived from the included records — a range
// where they differ, with the marker at a representative median — so the
// reviewer resolves a disagreement between recordings by choosing which
// ones the card includes and saving it. Only accepted recordings are
// offered: the report can narrow a card's set, never widen it. A card whose
// document carries no records (an older snapshot) keeps its imported value.
//
// Deviations: `data.features` global -> explicit `features` argument; the
// refusal to untick the last recording is returned as text (upstream shows
// an alert); a set whose values coincide leaves the card a single value
// (upstream keeps a degenerate range); Cycle Regularity grades the ± timing
// swing (half the spread this platform's records carry; upstream's records
// hold the swing itself since cardiac-panel-v4 git 94432d0) against the
// card's own bands — the ones its marker is placed by, ±8% / ±18% unless the
// evidence brought others (upstream grades the selection against 8 / 18
// whatever bands the marker uses); the marker of a heart-rate range sits
// midway between its two ends, as the range edit places it; the Heart Rate
// card's value is worded in whole bpm, as the roll-up words it (the
// checklist rows keep each recording's recorded decimals).
//
// A heart rate the HCP entered in the viewer (Medaica Report v0.6.1) reaches
// the card already substituted into the recording's record by the roll-up,
// so the derived value needs nothing special; the card keeps saying where
// the rate came from: its PCG Viewer source line (`heartRateSourceFinding`)
// and the record's row ("HCP confirmed"). Deviation: upstream's toggle
// recalculates from the automated rates and loses the confirmed value.

import {
  currentHeartRateBpm,
  hasAcceptedHeartRateRange,
  heartRateRangeOf,
  isCardiacCycleTimingFeature,
  isHeartRateFeature,
  isRhythmRegularityFeature,
  isSysDiaDurationFeature,
  sourceIdForFeed,
} from "./features"
import {
  cardiacCycleTimingColorClass,
  getColorClass,
  heartRateRangeGrades,
  sysDiaDurationStatusFromValues,
  worstStatus,
} from "./status"
import {
  calculateMarkerPosition,
  displayMarkerPosition,
  heartRateMarkerPosition,
  heartRateRangeMarkerPosition,
} from "./marker"
import { pcgTimingDisplayLabel, rhythmRegularityThresholds, timingPatternLabelForStatus } from "./timing-pattern"
import { syncNotesAfterStatusChange } from "./review"
import { ENGINE_RATIO_CALCULATION } from "../../pcg-viewer/core/timing"

export const TIMING_SOURCE_KEEP_ONE_HINT =
  "Keep at least one source recording ticked, or exclude the entire card from the report."

/** The roll-up's `acceptedSource` for a heart rate the HCP entered in the viewer. */
export const HCP_ENTERED_VIEWER_SOURCE = "hcp_entered_viewer"
/** An automated heart rate the timing engine measured over its Evaluation Range (upstream's label). */
export const VIEWER_EVAL_RANGE_SOURCE = "viewer_eval_range"
/** An automated heart rate over the viewer's selected usable cycles (a guide without an Evaluation Range). */
export const VIEWER_CURATED_CYCLES_SOURCE = "viewer_curated_cycles"
/** The automated rates behind an exam-level value come from both of the sources above. */
export const MIXED_AUTOMATED_SOURCE = "mixed"

const AUTOMATED_SOURCES = new Set([VIEWER_EVAL_RANGE_SOURCE, VIEWER_CURATED_CYCLES_SOURCE, MIXED_AUTOMATED_SOURCE])

/**
 * Where a Heart Rate block's automated rate came from: the roll-up names it
 * (`automatedSource`); a viewer package names it as its `acceptedSource`
 * while the rate is the automated one, and otherwise (a reviewer-confirmed
 * rate) by what its live-rate comparison was made against. Null when the
 * block does not say.
 */
export function automatedHeartRateSourceOf(heartRate) {
  if (!heartRate) return null
  if (AUTOMATED_SOURCES.has(heartRate.automatedSource)) return heartRate.automatedSource
  if (AUTOMATED_SOURCES.has(heartRate.acceptedSource)) return heartRate.acceptedSource
  const comparedTo = heartRate.supportingLiveBpmReview?.comparedTo
  if (comparedTo === "viewer_eval_range_bpm") return VIEWER_EVAL_RANGE_SOURCE
  if (comparedTo === "viewer_curated_cycle_bpm") return VIEWER_CURATED_CYCLES_SOURCE
  return null
}

/** SYS / DIA durations that are the timing engine's medians over its Evaluation Range. */
export const MEDIAN_DURATIONS = "median"
/** SYS / DIA durations that are means over the viewer's usable cycles (a guide without the timing engine). */
export const MEAN_DURATIONS = "mean"

/**
 * Which statistic a viewer package's timing block (`cardiacCycleTiming` /
 * `systolicDiastolicTiming`) reports its SYS / DIA durations as: the timing
 * engine's medians when its calculation method names the engine, else means
 * over the usable cycles, which every viewer guide without the engine
 * reports. Null without a block.
 */
export function durationStatisticOf(timingBlock) {
  if (!timingBlock) return null
  return timingBlock.calculationMethod === ENGINE_RATIO_CALCULATION ? MEDIAN_DURATIONS : MEAN_DURATIONS
}

/** The statistic every one of `statistics` names, else null (none, a mix, or one unknown). */
export function sharedDurationStatistic(statistics) {
  const distinct = new Set(statistics)
  return distinct.size === 1 ? [...distinct][0] : null
}

export function isTimingReviewFeature(feature) {
  return (
    !!feature &&
    (isHeartRateFeature(feature) ||
      isCardiacCycleTimingFeature(feature) ||
      isSysDiaDurationFeature(feature) ||
      isRhythmRegularityFeature(feature))
  )
}

/** The mean of the finite numbers among `values`; null when there is none. */
export function averageNumber(values) {
  const finite = values.filter((value) => Number.isFinite(value))
  return finite.length ? finite.reduce((sum, value) => sum + value, 0) / finite.length : null
}

export function medianNumber(values) {
  const sorted = (values || []).filter(Number.isFinite).sort((a, b) => a - b)
  if (!sorted.length) return null
  const middle = Math.floor(sorted.length / 2)
  return sorted.length % 2 ? sorted[middle] : (sorted[middle - 1] + sorted[middle]) / 2
}

const timingNumeric = (value) =>
  value !== null && value !== undefined && value !== "" && Number.isFinite(Number(value)) ? Number(value) : null

// A record's missing number stays missing: it never counts as 0
const presentValues = (sources, key) => sources.map((source) => timingNumeric(source[key])).filter((v) => v !== null)

/** The records a timing card carries; empty on a card without per-recording data. */
export function timingSourceRecordings(feature) {
  return Array.isArray(feature?.timingSourceRecordings) ? feature.timingSourceRecordings : []
}

export function selectedTimingSources(feature) {
  return timingSourceRecordings(feature).filter((source) => source.included !== false)
}

// The recorded timing spread is the full width of the selected cycles'
// variation; the card shows and grades half of it, the ± swing.
const swingOf = (source) => {
  const spread = timingNumeric(source.robustSpreadPct)
  return spread === null ? null : spread / 2
}

const bpmText = (value) => Number(value).toFixed(1).replace(/\.0$/, "")

/** A record whose rate is one the HCP entered in the viewer (a real value, never a coerced null). */
export const timingSourceConfirmedBpm = (source) =>
  source && (timingNumeric(source.hcpConfirmedBpm) !== null || source.heartRateSource === HCP_ENTERED_VIEWER_SOURCE)
    ? timingNumeric(source.hcpConfirmedBpm) ?? timingNumeric(source.valueBpm)
    : null

/**
 * Whether a Heart Rate card's accepted rate is one the HCP entered in the
 * viewer: the roll-up says so, and a value is actually present — on one of
 * the records the card includes, or on the card when it carries no records.
 */
export function heartRateConfirmedInViewer(feature) {
  if (!isHeartRateFeature(feature) || feature.acceptedSource !== HCP_ENTERED_VIEWER_SOURCE) return false
  const records = timingSourceRecordings(feature)
  if (records.length) return selectedTimingSources(feature).some((source) => timingSourceConfirmedBpm(source) !== null)
  return timingNumeric(feature.hcpConfirmedBpm) !== null
}

/**
 * The PCG Viewer source line of a Heart Rate card: a rate the HCP entered
 * in the viewer says so, with the automated estimate it replaced (named as
 * the Evaluation Range's when that is where it came from) and the
 * reviewer's note; otherwise the rate across the included recordings.
 */
export function heartRateSourceFinding(feature) {
  if (heartRateConfirmedInViewer(feature)) {
    const estimate =
      feature.automatedSource === VIEWER_EVAL_RANGE_SOURCE ? "Automated Eval Range estimate" : "Automated estimate"
    const automated = feature.automatedObserved ? ` ${estimate}: ${feature.automatedObserved}.` : ""
    const note = feature.acceptedBpmReviewNote ? ` ${feature.acceptedBpmReviewNote}` : ""
    return `Accepted BPM entered in Viewer: ${feature.observed}.${automated}${note}`
  }
  return `Heart rate across included recordings: ${feature.observed}.`
}
const ratioText = (sdRatio) => `1 : ${(1 / Number(sdRatio)).toFixed(1)}`
const msText = (value) => `${Math.round(Number(value))} ms`
// "252 ms", or "252–294 ms" when the rounded ends differ
const msRangeText = (values) => {
  const min = Math.round(Math.min(...values))
  const max = Math.round(Math.max(...values))
  return min === max ? `${min} ms` : `${min}–${max} ms`
}

/** One record's value in the card's own terms, for the checklist row. */
export function timingSourceValue(feature, source) {
  if (isHeartRateFeature(feature)) {
    const confirmed = timingSourceConfirmedBpm(source)
    if (confirmed !== null) return `${bpmText(confirmed)} bpm · HCP confirmed`
    const bpm = timingNumeric(source.valueBpm)
    return bpm === null ? "Not available" : `${bpmText(bpm)} bpm`
  }
  if (isCardiacCycleTimingFeature(feature)) {
    return Number(source.sdRatio) > 0 ? ratioText(source.sdRatio) : "Not available"
  }
  if (isSysDiaDurationFeature(feature)) {
    const sys = timingNumeric(source.sysMs)
    const dia = timingNumeric(source.diaMs)
    return `SYS ${sys === null ? "—" : msText(sys)} · DIA ${dia === null ? "—" : msText(dia)}`
  }
  const swing = swingOf(source)
  const rrCv = timingNumeric(source.rrCv)
  if (swing !== null) return `±${swing.toFixed(1)}% timing variation`
  return rrCv === null ? "Not available" : `Cycle CV ${(rrCv * 100).toFixed(1)}%`
}

/** The range the included records span, for the checklist heading. */
export function timingSourceSelectionSummary(feature) {
  const selected = selectedTimingSources(feature)
  if (!selected.length) return "No source recordings included"
  if (isHeartRateFeature(feature)) {
    const values = presentValues(selected, "valueBpm")
    if (!values.length) return "Heart-rate values unavailable"
    return `Included range: ${bpmText(Math.min(...values))}–${bpmText(Math.max(...values))} bpm`
  }
  if (isCardiacCycleTimingFeature(feature)) {
    const values = selected
      .map((source) => Number(source.sdRatio))
      .filter((value) => Number.isFinite(value) && value > 0)
      .map((value) => 1 / value)
    if (!values.length) return "SYS:DIA values unavailable"
    return `Included range: 1 : ${Math.min(...values).toFixed(1)}–${Math.max(...values).toFixed(1)}`
  }
  if (isSysDiaDurationFeature(feature)) {
    const sys = presentValues(selected, "sysMs")
    const dia = presentValues(selected, "diaMs")
    const phase = (values) =>
      values.length ? `${Math.round(Math.min(...values))}–${Math.round(Math.max(...values))} ms` : "—"
    return `Included range: SYS ${phase(sys)} · DIA ${phase(dia)}`
  }
  const swings = selected.map(swingOf).filter((value) => value !== null)
  if (swings.length) {
    return `Included range: ±${Math.min(...swings).toFixed(1)}–${Math.max(...swings).toFixed(1)}% timing variation`
  }
  const cvValues = selected
    .map((source) => timingNumeric(source.rrCv))
    .filter((value) => value !== null)
    .map((value) => value * 100)
  return cvValues.length
    ? `Included cycle-CV range: ${Math.min(...cvValues).toFixed(1)}–${Math.max(...cvValues).toFixed(1)}%`
    : "Cycle-regularity values unavailable"
}

const AI_STATUS_BY_COLOR = { green: "normal", red: "abnormal" }
const aiStatusOf = (status) => AI_STATUS_BY_COLOR[status] || "borderline"

const applyHeartRateValues = (features, feature, selected, hrBpm) => {
  const values = presentValues(selected, "valueBpm").map((value) => Math.round(value))
  if (!values.length) return false
  const range = { min: Math.min(...values), max: Math.max(...values) }
  if (range.min === range.max) {
    // One value: a single marker. It replaces the accepted range as the
    // card's assessment when there was one; otherwise nothing is replaced.
    feature.observed = `${range.min} bpm`
    feature.clinicianRangeBpm = null
    feature.clinicianAssessmentMode = hasAcceptedHeartRateRange(feature) ? "single" : null
    feature.mixedRange = false
    feature.status = getColorClass(feature.name, feature.observed, feature.normalRange, null, feature.scaleType)
    feature.markerPosition = calculateMarkerPosition(feature, hrBpm)
  } else {
    feature.observed = `${range.min}–${range.max} bpm`
    feature.clinicianRangeBpm = range
    feature.clinicianAssessmentMode = "range"
    const grades = heartRateRangeGrades(range, feature.normalRange, feature.scaleType)
    feature.status = grades.status
    feature.mixedRange = grades.mixed
    feature.markerPosition = heartRateRangeMarkerPosition(feature, range, hrBpm)
  }
  const pcgSource = (feature.sources || []).find((source) => sourceIdForFeed(source) === "pcg_viewer")
  if (pcgSource) {
    // The source line follows the included recordings; the package's
    // supporting-context sentences stay on it
    const contextNotes = Array.isArray(feature.supportingContextNotes) ? feature.supportingContextNotes : []
    pcgSource.finding = [heartRateSourceFinding(feature), ...contextNotes].join(" ")
  }
  return true
}

const applyRatioValues = (feature, selected, hrBpm) => {
  const rawValues = selected
    .map((source) => Number(source.sdRatio))
    .filter((value) => Number.isFinite(value) && value > 0)
  if (!rawValues.length) return false
  const displayValues = rawValues.map((value) => 1 / value)
  const min = Math.min(...displayValues)
  const max = Math.max(...displayValues)
  const single = min.toFixed(1) === max.toFixed(1)
  feature.observed = single ? `1 : ${min.toFixed(1)}` : `1 : ${min.toFixed(1)}–${max.toFixed(1)}`
  feature.clinicianRatioRange = single ? null : { min, max }
  feature.representativeSdRatio = medianNumber(rawValues)
  const grades = rawValues.map((value) => cardiacCycleTimingColorClass(ratioText(value), feature.status, hrBpm))
  feature.status = worstStatus(grades)
  feature.mixedRange = new Set(grades).size > 1
  feature.markerPosition = calculateMarkerPosition(feature, hrBpm)
  return true
}

const applyDurationValues = (feature, selected, hrBpm) => {
  const sysValues = presentValues(selected, "sysMs")
  const diaValues = presentValues(selected, "diaMs")
  if (!sysValues.length && !diaValues.length) return false
  const phase = (values) => (values.length ? msRangeText(values) : "—")
  feature.observed = `SYS ${phase(sysValues)} · DIA ${phase(diaValues)}`
  feature.clinicianSysDiaDurationRange = {
    sysMin: sysValues.length ? Math.min(...sysValues) : null,
    sysMax: sysValues.length ? Math.max(...sysValues) : null,
    diaMin: diaValues.length ? Math.min(...diaValues) : null,
    diaMax: diaValues.length ? Math.max(...diaValues) : null,
  }
  feature.representativeSysMs = medianNumber(sysValues)
  feature.representativeDiaMs = medianNumber(diaValues)
  feature.status = worstStatus(
    selected.map((source) =>
      sysDiaDurationStatusFromValues(timingNumeric(source.sysMs), timingNumeric(source.diaMs), hrBpm, feature.status)
    )
  )
  feature.markerPosition = calculateMarkerPosition(feature, hrBpm)
  return true
}

const applyRegularityValues = (feature, selected, hrBpm) => {
  const spreads = selected.map((source) => timingNumeric(source.robustSpreadPct)).filter((value) => value !== null)
  const cvValues = selected
    .map((source) => timingNumeric(source.rrCv))
    .filter((value) => value !== null)
    .map((value) => value * 100)
  const usesCycleCv = !spreads.length
  // The mean timing variation across the included recordings (Heart Report
  // v0.5.8 at cardiac-panel-v4 git ebcfc04; the roll-up uses the same mean),
  // so the marker, status, interpretation and provider wording agree
  const value = averageNumber(usesCycleCv ? cvValues : spreads)
  if (!Number.isFinite(value)) return false
  if (usesCycleCv) {
    // Without a timing spread the cycle CV stands in, and the card asks for
    // a look rather than grading it
    feature.status = "amber"
    feature.timingPatternLabel = timingPatternLabelForStatus(feature.status)
    feature.observed = `Median cycle CV ${value.toFixed(1)}% · review`
    feature.timingVariabilityPct = null
    feature.robustSpreadPct = null
    feature.timingSwingPct = null
  } else {
    const swing = value / 2
    const thresholds = rhythmRegularityThresholds(feature)
    if (swing <= thresholds.normal) feature.status = "green"
    else if (swing <= thresholds.elevated) feature.status = "amber"
    else feature.status = "red"
    feature.timingPatternLabel = timingPatternLabelForStatus(feature.status)
    feature.observed = `±${swing.toFixed(1)}% timing variation · ${pcgTimingDisplayLabel(feature.timingPatternLabel)}`
    feature.timingVariabilityPct = value
    feature.robustSpreadPct = value
    feature.timingSwingPct = swing
  }
  feature.markerPosition = calculateMarkerPosition(feature, hrBpm)
  return true
}

/**
 * Sets a timing card's value, status and marker from the records it
 * includes; returns whether the records gave it a value. No notes or flags
 * are touched — `recalculateTimingFeatureFromSources` is the whole step.
 */
export function applyTimingSourceValues(features, feature) {
  const selected = selectedTimingSources(feature)
  if (!selected.length) return false
  const hrBpm = currentHeartRateBpm(features)
  let applied = false
  if (isHeartRateFeature(feature)) applied = applyHeartRateValues(features, feature, selected, hrBpm)
  else if (isCardiacCycleTimingFeature(feature)) applied = applyRatioValues(feature, selected, hrBpm)
  else if (isSysDiaDurationFeature(feature)) applied = applyDurationValues(feature, selected, hrBpm)
  else if (isRhythmRegularityFeature(feature)) applied = applyRegularityValues(feature, selected, hrBpm)
  if (applied) feature.aiStatus = aiStatusOf(feature.status)
  return applied
}

/**
 * Re-derives a timing card from the records it includes and brings its
 * notes along. With `markEdited` (a reviewer's toggle) the card counts as
 * the clinician's assessment and loses any approval; without it (import) it
 * stays the evidence's. Returns whether the card took a value.
 */
export function recalculateTimingFeatureFromSources(features, index, markEdited = true) {
  const feature = features[index]
  if (!feature || !applyTimingSourceValues(features, feature)) return false
  if (markEdited) {
    feature.reviewTouched = true
    feature.manualAssessmentStarted = true
    feature.approved = false
  }
  syncNotesAfterStatusChange(features, index)
  return true
}

/** Whether a timing card's value is the one its included records give (nothing typed over it). */
export function timingValuesDerivedFromSources(features, feature) {
  if (!timingSourceRecordings(feature).length) return false
  const probe = { ...feature, sources: [] }
  return applyTimingSourceValues(features, probe) && probe.observed === feature.observed
}

/**
 * Ticks or unticks one recording on a timing card and re-derives the card.
 * The last ticked recording cannot be unticked: the card is excluded
 * instead. Returns {ok, reason}.
 */
export function toggleTimingSource(
  features,
  index,
  recordingId,
  included,
  { now = () => new Date().toISOString() } = {}
) {
  const feature = features[index]
  const source = timingSourceRecordings(feature).find((item) => String(item.recordingId) === String(recordingId))
  if (!source) return { ok: false, reason: null }
  if (!included && selectedTimingSources(feature).length <= 1) {
    return { ok: false, reason: TIMING_SOURCE_KEEP_ONE_HINT }
  }
  source.included = !!included
  source.selectionChangedAt = now()
  recalculateTimingFeatureFromSources(features, index, true)
  return { ok: true, reason: null }
}

/**
 * Re-applies an earlier selection to a rebuilt record list: every recording
 * in `excluded` (recordingId -> selectionChangedAt) that is still offered is
 * unticked, except that at least one recording stays ticked. Returns how
 * many were unticked.
 */
export function reapplyTimingSelection(feature, excluded) {
  const records = timingSourceRecordings(feature)
  let applied = 0
  records.forEach((source) => {
    const changedAt = excluded?.get(String(source.recordingId))
    if (changedAt === undefined || records.filter((item) => item.included !== false).length <= 1) return
    source.included = false
    source.selectionChangedAt = changedAt
    applied += 1
  })
  return applied
}

/** A typed value or a moved marker replaces the range a SYS:DIA card derived from its records. */
export function clearCycleTimingRange(feature) {
  if (isCardiacCycleTimingFeature(feature)) {
    feature.clinicianRatioRange = null
    feature.representativeSdRatio = null
    feature.mixedRange = false
  }
  if (isSysDiaDurationFeature(feature)) {
    feature.clinicianSysDiaDurationRange = null
    feature.representativeSysMs = null
    feature.representativeDiaMs = null
  }
}

// A single-value marker position for the feature, ignoring any range it carries
const singleValuePosition = (feature, observed, hrBpm) =>
  displayMarkerPosition(
    calculateMarkerPosition(
      {
        ...feature,
        observed,
        markerPosition: undefined,
        reviewTouched: false,
        clinicianRatioRange: null,
        clinicianSysDiaDurationRange: null,
      },
      hrBpm
    )
  )

/**
 * The two ends a timing card shows on its scale, as positions (percent
 * from the left, already clamped to the track) with their titles, or null
 * for a single-marker card: a Heart Rate card's range, a SYS:DIA Ratio
 * card's, or the spread of a SYS:DIA Duration card's included recordings
 * (only when more than one recording places differently).
 */
export function timingRangeOf(feature, hrBpm) {
  if (!feature) return null
  const heartRange = heartRateRangeOf(feature)
  if (heartRange) {
    return {
      minPos: displayMarkerPosition(heartRateMarkerPosition(feature, heartRange.min, hrBpm)),
      maxPos: displayMarkerPosition(heartRateMarkerPosition(feature, heartRange.max, hrBpm)),
      minLabel: `Minimum ${heartRange.min} bpm`,
      maxLabel: `Maximum ${heartRange.max} bpm`,
    }
  }
  if (isCardiacCycleTimingFeature(feature) && feature.clinicianRatioRange) {
    const low = Number(feature.clinicianRatioRange.min)
    const high = Number(feature.clinicianRatioRange.max)
    if (Number.isFinite(low) && Number.isFinite(high) && low < high) {
      return {
        minPos: singleValuePosition(feature, `1 : ${low}`, hrBpm),
        maxPos: singleValuePosition(feature, `1 : ${high}`, hrBpm),
        minLabel: `Minimum 1 : ${low.toFixed(1)}`,
        maxLabel: `Maximum 1 : ${high.toFixed(1)}`,
      }
    }
  }
  if (isSysDiaDurationFeature(feature) && feature.clinicianSysDiaDurationRange) {
    const phaseText = (value) => (value === null ? "—" : msText(value))
    const pairText = (sys, dia) => `SYS ${phaseText(sys)} · DIA ${phaseText(dia)}`
    const selected = selectedTimingSources(feature)
    // The recordings' own pairs span the band while the card still shows
    // what they derive; a range the clinician typed spans its own ends
    const probe = { ...feature }
    const derived =
      selected.length > 0 && applyDurationValues(probe, selected, hrBpm) && probe.observed === feature.observed
    const pairs = derived
      ? selected.map((source) => [timingNumeric(source.sysMs), timingNumeric(source.diaMs)])
      : [
          [
            timingNumeric(feature.clinicianSysDiaDurationRange.sysMin),
            timingNumeric(feature.clinicianSysDiaDurationRange.diaMin),
          ],
          [
            timingNumeric(feature.clinicianSysDiaDurationRange.sysMax),
            timingNumeric(feature.clinicianSysDiaDurationRange.diaMax),
          ],
        ]
    const positions = pairs
      .filter(([sys, dia]) => sys !== null || dia !== null)
      .map(([sys, dia]) => {
        const observed = pairText(sys, dia)
        return { position: singleValuePosition(feature, observed, hrBpm), label: observed }
      })
      .sort((a, b) => a.position - b.position)
    const first = positions[0]
    const last = positions[positions.length - 1]
    if (positions.length > 1 && first.position !== last.position) {
      return { minPos: first.position, maxPos: last.position, minLabel: first.label, maxLabel: last.label }
    }
  }
  return null
}
